import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Search } from "lucide-react";
import { AnimatePresence, motion } from "framer-motion";
import Seo from "@/components/Seo";
import { PageHero } from "@/pages/About";
import { CtaBand } from "@/pages/Home";
import { PropertyCard } from "@/components/PropertyCard";
import { useLanguage } from "@/context/LanguageContext";
import { useListings } from "@/hooks/useListings";

export default function Listings() {
  const { t } = useLanguage();
  const { listings, loaded } = useListings();
  const [searchParams] = useSearchParams();
  const [filter, setFilter] = useState(searchParams.get("status") || "all");
  const [query, setQuery] = useState(searchParams.get("q") || "");

  useEffect(() => {
    setFilter(searchParams.get("status") || "all");
    setQuery(searchParams.get("q") || "");
  }, [searchParams]);

  const tabs = [
    { key: "all", label: t.listingsPage.all },
    { key: "sale", label: t.listingsPage.forSale },
    { key: "sold", label: t.listingsPage.sold },
    { key: "lease", label: t.listingsPage.forLease },
  ];

  const filtered = listings
    .filter((l) => (filter === "all" ? true : l.status === filter))
    .filter((l) => {
      const q = query.trim().toLowerCase();
      if (!q) return true;
      return `${l.address} ${l.neighborhood}`.toLowerCase().includes(q);
    });

  return (
    <main data-testid="listings-page">
      <Seo
        title="South Florida Homes for Sale — Carlos Vindel Realty"
        siteName="Carlos Vindel Realty"
        description="Browse homes for sale, recently sold properties, and leases across Coral Gables, Brickell, Coconut Grove, Doral, Kendall, and Miami Beach."
      />
      <PageHero testid="listings-hero" overline={t.listingsPage.overline} title={t.listingsPage.title} subtitle={t.listingsPage.subtitle} />

      <section className="bg-cream py-16 sm:py-24 min-h-[50vh]">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <div className="mb-8 flex items-center bg-white border border-sand rounded-full p-1.5 pl-6 shadow-[0_4px_24px_rgba(13,19,26,0.06)] max-w-2xl">
            <Search className="w-4 h-4 text-gold shrink-0" />
            <input
              data-testid="listings-search-input"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={t.hero.searchPlaceholder}
              className="flex-1 min-w-0 bg-transparent text-ink text-sm placeholder:text-[#8C96A4] focus:outline-none py-2.5 px-3"
            />
          </div>

          <div data-testid="listings-filter-tabs" className="flex flex-wrap gap-2 mb-12">
            {tabs.map((tab) => (
              <button
                key={tab.key}
                data-testid={`filter-tab-${tab.key}`}
                onClick={() => setFilter(tab.key)}
                className={`text-xs uppercase tracking-[0.2em] font-semibold px-5 py-2.5 rounded-full border transition-all duration-300 ${
                  filter === tab.key
                    ? "bg-ink text-cream border-ink"
                    : "bg-transparent text-[#525D6B] border-sand hover:border-gold hover:text-gold-dark"
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>

          <motion.div layout className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            <AnimatePresence mode="popLayout">
              {filtered.map((l, i) => (
                <motion.div key={l.id} layout exit={{ opacity: 0, scale: 0.96 }} transition={{ duration: 0.3 }}>
                  <PropertyCard listing={l} index={i} />
                </motion.div>
              ))}
            </AnimatePresence>
          </motion.div>

          {filtered.length === 0 && loaded && (
            <p data-testid="listings-empty" className="text-center text-[#8C96A4] py-20 font-serif italic text-xl">
              {t.listingsPage.empty}
            </p>
          )}
        </div>
      </section>

      <CtaBand />
    </main>
  );
}
